import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useI18N } from "@sundae/react-hooks";
import { Button, ChevronRightIcon, DateTimePicker, DownloadIcon, getLocalTimeZone, Image, Input, Loader, Notice, now, PlusCircledIcon, Switch, Tabs, Text, TextArea, } from "@sundae/ui-toolkit";
import { useWalletData, useWalletObserver } from "@sundaeswap/wallet-lite";
import { useNavigate } from "@tanstack/react-location";
import cx from "classnames";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import camelCase from "lodash/camelCase";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { EPATHS } from "../../../constants/paths";
import { getProposalsSdk } from "../../../graphql/client/proposals";
import { ProposalType } from "../../../graphql/generated/proposals.sdk";
import { useBrandConfig } from "../../../hooks";
import { useAppConfig } from "../../../hooks/useAppConfig";
import { useCreateProposal } from "../../../hooks/useCreateProposal";
import { useEnvConfig } from "../../../hooks/useEnvConfig";
import { unixToSlot } from "../../../utils";
import { esc_p } from "../../../utils/esc";
import { getProposalPayload } from "../../../utils/payloads";
import ProposalDescription from "../../ProposalDescription";
import { MAX_DESC_LENGTH } from "./constants";
import { ChoiceInput, SectionTitle } from "./modules";
import { FormError } from "./modules/FormError";
import { ForumInput } from "./modules/ForumInput";
const DEFAULT_VOTE_UNIT = "day";
const DEFAULT_VOTE_END = 15;
const currentDate = now(getLocalTimeZone());
export const CreateProposalForm = () => {
    const { t } = useI18N("createProposal");
    const brand = useBrandConfig();
    const { stakeAddress } = useWalletObserver();
    const { signData } = useWalletData();
    const navigate = useNavigate();
    const config = useAppConfig();
    const env = useEnvConfig();
    const titleInputRef = useRef(null);
    const [title, setTitle] = useState("");
    const [desc, setDesc] = useState("");
    const { proposals: { required_inputs: { forumMeta }, }, } = useAppConfig();
    const [forumLink, setForumLink] = useState(forumMeta.prefix);
    const [forumLinkEnabled, setForumLinkEnabled] = useState(true);
    const [previewTab, setPreviewTab] = useState("Write");
    const voteStartCount = config?.proposals?.datePolicy?.voteStart?.offset?.count ?? 0;
    const voteStartUnit = config?.proposals?.datePolicy?.voteStart?.offset?.unit ?? DEFAULT_VOTE_UNIT;
    const [voteStartDate, setVoteStartDate] = useState(currentDate.add({ [`${voteStartUnit}s`]: voteStartCount }));
    const voteEndCount = config?.proposals?.datePolicy?.voteEnd?.offset?.count ?? DEFAULT_VOTE_END;
    const voteEndUnit = config?.proposals?.datePolicy?.voteEnd?.offset?.unit ?? DEFAULT_VOTE_UNIT;
    const [voteEndDate, setVoteEndDate] = useState(currentDate.add({ [`${voteEndUnit}s`]: voteEndCount }));
    const voteSnapshotCount = config?.proposals?.datePolicy?.voteSnapshot?.offset?.count ?? DEFAULT_VOTE_END;
    const voteSnapshotUnit = config?.proposals?.datePolicy?.voteSnapshot?.offset?.unit ?? DEFAULT_VOTE_UNIT;
    const [voteSnapshotDate, setVoteSnapshotDate] = useState(currentDate.add({ [`${voteSnapshotUnit}s`]: voteSnapshotCount }));
    const [descLen, setDescLen] = useState(0);
    const [tempChoice, setTempChoice] = useState("");
    const [tempBulk, setTempBulk] = useState("");
    const [formError, setRealFormError] = useState();
    useEffect(() => {
        titleInputRef.current?.focus();
    }, []);
    const setFormError = (data, timeout = 5000) => {
        setRealFormError(data);
        setTimeout(() => {
            setRealFormError(null);
        }, timeout);
    };
    const [options, setOptions] = useState(Object.values([
        ...(config?.proposals?.required_inputs?.choices?.map(({ name }) => ({
            key: uuidv4(),
            name,
        })) ?? []),
    ]));
    const { mutateAsync: createProposal, isLoading, isError, isSuccess, data } = useCreateProposal();
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Validate.
        if (forumLinkEnabled && forumLink === forumMeta.prefix) {
            setFormError({
                context: "forumLink",
                message: t("errors.forum_link.no_value"),
            });
            return;
        }
        if (title.length < 1) {
            setFormError({
                context: "title",
                message: t("errors.title.no_value"),
            });
        }
        if (desc.length < 1) {
            setFormError({
                context: "desc",
                message: t("errors.description.no_value"),
            });
        }
        const { getCurrentSlot } = await getProposalsSdk();
        const currentSlot = await getCurrentSlot();
        dayjs.extend(duration);
        function dateToSlot(date) {
            const dateSeconds = Math.floor(date.toDate("UTC").valueOf() / 1000);
            return unixToSlot(dateSeconds).toString();
        }
        const optionsWithTemp = options.map(({ name }) => name);
        if (tempChoice && !optionsWithTemp.includes(tempChoice)) {
            optionsWithTemp.push(tempChoice);
        }
        try {
            const proposal = {
                author: stakeAddress,
                createdAt: currentSlot.now.slot,
                description: esc_p(desc),
                network: env.envName,
                options: optionsWithTemp,
                title: title,
                discussion: forumLinkEnabled ? forumLink : "",
                project: env.project,
                type: ProposalType.Choice,
                version: "1",
                voteEnd: dateToSlot(voteEndDate),
                voteStart: dateToSlot(voteStartDate),
                voteSnapshot: dateToSlot(voteSnapshotDate),
            };
            const payloadToSign = getProposalPayload(proposal);
            const { signature, payload, key } = await signData({
                signingAddress: stakeAddress,
                payload: payloadToSign,
            });
            if (!signature) {
                throw new Error("Signature invalid!");
            }
            await createProposal({
                proposal,
                proof: {
                    cosePayload: payload,
                    signature,
                    key,
                },
            });
        }
        catch (e) {
            console.log(e);
        }
    };
    const handleBulkInput = async () => {
        if (tempBulk === "") {
            setFormError({
                context: "choices",
                message: t("errors.choices.no_value"),
            });
            return;
        }
        const options = [];
        const delimiter = tempBulk.includes(',') ? ',' : /\n/;
        const optionItems = tempBulk.split(delimiter).map(item => item.trim());
        let success = true;
        for (const name of optionItems) {
            const key = camelCase(name);
            if (options.find(({ key: storedKey }) => storedKey === key)) {
                setFormError({
                    context: "choices",
                    message: `You have a duplicate value in your bulk import: ${name}. Please remove and try again.`,
                });
                success = false;
                break;
            }
            options.push({
                key,
                name,
            });
        }
        if (!success) {
            return;
        }
        setOptions((prev) => [...prev, ...options]);
        setTempBulk("");
    };
    if (isLoading) {
        return (_jsxs("div", { className: "flex h-full flex-col items-center gap-4", children: [_jsx(Loader, { size: "medium" }), _jsx(Text, { tag: "h4", size: "3xl", weight: "bold", children: t("states.submitting.title") }), _jsx(Text, { tag: "p", children: t("states.submitting.description") })] }));
    }
    if (isSuccess && data?.createProposal?.hash) {
        return (_jsxs("div", { className: "flex flex-col items-center gap-4 lg:flex-row", children: [_jsx("div", { className: "lg:w-2/3", children: _jsx(Image, { src: config.brand.images.proposal_created, className: "w-full", alt: t("states.success.title") }) }), _jsxs("div", { className: "lg:w-1/3", children: [_jsx(Text, { tag: "h4", size: "3xl", weight: "bold", className: "mb-2", children: t("states.success.title") }), _jsx(Text, { tag: "p", children: t("states.success.description") }), _jsxs(Button, { className: cx("mt-8 flex items-center gap-4", brand?.classes.buttons.all, brand?.classes.buttons.colored), variant: "colored", onClick: () => {
                                navigate({
                                    to: EPATHS.PROPOSAL_DETAIL,
                                    hash: data?.createProposal?.hash,
                                });
                            }, children: [t("states.success.action"), _jsx(ChevronRightIcon, {})] })] })] }));
    }
    return (_jsxs("form", { action: "", onSubmit: handleSubmit, children: [_jsxs("div", { className: "flex w-full flex-wrap items-center justify-center gap-4", children: [_jsx(SectionTitle, { id: "proposal-title", srOnly: true, children: t("labels.title.title") }), _jsx(Input, { ref: titleInputRef, value: title, onChange: ({ currentTarget }) => setTitle(currentTarget.value), name: "title", "aria-labelledby": "proposal-title", placeholder: "Proposal title...", className: "py-4" })] }), _jsxs("div", { className: "mb-8 flex flex-col items-start gap-8 md:flex-row", children: [_jsxs("div", { className: "mt-8 w-full md:w-1/2", children: [_jsx(SectionTitle, { id: "proposal-desc", children: t("labels.description") }), _jsx(Tabs, { defaultValue: previewTab, value: previewTab, onValueChange: setPreviewTab, data: [
                                    {
                                        id: "Write",
                                        trigger: _jsx(_Fragment, { children: "Write" }),
                                        content: (_jsxs(_Fragment, { children: [_jsx(TextArea, { value: desc, onChange: ({ currentTarget }) => {
                                                        setDesc(currentTarget.value.slice(0, MAX_DESC_LENGTH));
                                                        setDescLen(currentTarget.value.slice(0, MAX_DESC_LENGTH).length);
                                                    }, name: "desc", placeholder: t("labels.description_placeholder"), rows: 10, "aria-labelledby": "proposal-desc" }), _jsxs(Text, { tag: "p", variant: "muted", size: "sm", className: "w-full text-left", children: [descLen, "/", MAX_DESC_LENGTH, " ", t("labels.characters")] })] })),
                                    },
                                    {
                                        id: "Preview",
                                        trigger: _jsx(_Fragment, { children: "Preview" }),
                                        content: _jsx(ProposalDescription, { description: desc }),
                                    },
                                ] })] }), _jsxs("div", { className: "w-full md:mt-8 md:w-1/2", children: [_jsx(SectionTitle, { children: t("labels.choices") }), options.map(({ key, name }, index) => {
                                return (_jsx(ChoiceInput, { value: name, onChange: (e) => {
                                        setOptions((preVal) => {
                                            const newVal = [...preVal];
                                            newVal[index] = {
                                                key: key ?? uuidv4(),
                                                name: e.target.value.slice(0, 64),
                                            };
                                            return newVal;
                                        });
                                    }, removeEnabled: !config?.proposals?.required_inputs?.choices?.find(({ key: storedKey }) => storedKey === key), buttonProps: {
                                        onClick: () => setOptions((prevVal) => prevVal.filter(({ key: storedKey }) => storedKey !== key)),
                                    } }, key));
                            }), config?.features?.custom_options ? (_jsx(Tabs, { defaultValue: "single", data: [
                                    {
                                        id: "single",
                                        trigger: "Single Input",
                                        content: (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex items-stretch justify-between gap-2", children: _jsx(Input, { placeholder: t("labels.add_choice_placeholder"), value: tempChoice, onChange: (e) => {
                                                            setTempChoice(e.target.value.slice(0, 64));
                                                        } }) }), _jsxs(Button, { type: "button", disabled: !tempChoice, className: "mt-4 w-full", onClick: () => {
                                                        if (tempChoice === "") {
                                                            setFormError({
                                                                context: "choices",
                                                                message: t("errors.choices.no_value"),
                                                            });
                                                            return;
                                                        }
                                                        const newOption = {
                                                            key: camelCase(tempChoice),
                                                            name: tempChoice,
                                                        };
                                                        if (!options.find(({ key: storedKey }) => storedKey === newOption.key)) {
                                                            setTempChoice("");
                                                            setOptions((prevVal) => [...prevVal, newOption]);
                                                        }
                                                        else {
                                                            setFormError({
                                                                context: "choices",
                                                                message: "That value already exists!",
                                                            });
                                                        }
                                                    }, variant: "secondary", children: [t("labels.add_choice"), _jsx(PlusCircledIcon, {})] })] })),
                                    },
                                    {
                                        id: "bulk",
                                        trigger: "Bulk Import",
                                        content: (_jsxs(_Fragment, { children: [_jsx(TextArea, { placeholder: "Bulk import using a comma or newline delimitted string...", value: tempBulk, onChange: (e) => setTempBulk(e.target.value) }), _jsxs(Button, { type: "button", disabled: !tempBulk, className: "mt-4 w-full", onClick: handleBulkInput, variant: "secondary", children: [t("labels.bulk_import"), _jsx(DownloadIcon, {})] })] })),
                                    },
                                ] })) : (_jsx(Notice, { type: "info", className: "mt-2 text-sm", children: _jsx(Text, { tag: "span", children: t("messages.no_custom_options") }) })), _jsx(FormError, { context: "choices", error: formError })] })] }), _jsx(SectionTitle, { children: t("labels.settings") }), _jsxs("div", { className: "mb-8 grid grid-cols-3 gap-4 text-base lg:mb-16", children: [_jsxs("div", { className: "col-span-1 flex flex-col items-start gap-4", children: [_jsx(Text, { className: "w-full", tag: "p", size: "sm", variant: "muted", children: t("labels.vote", { context: "start" }) }), _jsx(DateTimePicker, { granularity: "minute", onChange: setVoteStartDate, value: voteStartDate })] }), _jsxs("div", { className: "col-span-1 flex flex-col items-start gap-4", children: [_jsx(Text, { className: "w-full", tag: "p", size: "sm", variant: "muted", children: t("labels.vote", { context: "end" }) }), _jsx(DateTimePicker, { granularity: "minute", minValue: voteStartDate, onChange: setVoteEndDate, value: voteEndDate })] }), _jsxs("div", { className: "col-span-1 flex flex-col items-start gap-4", children: [_jsx(Text, { className: "w-full", tag: "p", size: "sm", variant: "muted", children: t("labels.vote", { context: "snapshot" }) }), _jsx(DateTimePicker, { granularity: "minute", minValue: voteStartDate, onChange: setVoteSnapshotDate, value: voteSnapshotDate })] }), _jsx(FormError, { context: "settings", error: formError })] }), _jsx(SectionTitle, { children: _jsxs("div", { className: "flex items-center justify-start gap-4", children: [t("labels.forum"), !config?.proposals?.required_inputs?.forumLink && (_jsx(Switch, { checked: forumLinkEnabled, onCheckedChange: (checked) => setForumLinkEnabled(checked), size: "base" }))] }) }), forumLinkEnabled && (_jsx(ForumInput, { forumMeta: forumMeta, wrapperProps: {
                    className: "relative flex items-center justify-start overflow-hidden rounded-lg",
                }, inputProps: {
                    value: forumLink,
                    onChange: (e) => setForumLink(e.target.value),
                } })), _jsx(FormError, { context: "forumLink", error: formError }), _jsx(Button, { className: cx("mx-auto mt-8", brand?.classes.buttons.all, brand?.classes.buttons.colored), variant: "colored", disabled: isLoading || !desc || !title, children: t("actions.submit") }), isError && (_jsx(Text, { tag: "p", className: "mt-2", children: t("actions.error") }))] }));
};
export default CreateProposalForm;
//# sourceMappingURL=CreateProposalForm.js.map